import React, { useEffect, createElement } from 'react';
import { MapPinIcon, MessageCircleIcon, RefreshCwIcon, UserSearchIcon, PhoneIcon, DownloadIcon, ArrowRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
export function UDSAPlatform() {
  // Set page-specific meta tags
  useEffect(() => {
    // Store original values for cleanup
    const originalTitle = document.title;
    const metaDescription = document.querySelector('meta[name="description"]');
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const ogType = document.querySelector('meta[property="og:type"]');
    const ogImage = document.querySelector('meta[property="og:image"]');
    const ogImageAlt = document.querySelector('meta[property="og:image:alt"]');
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    const twitterImage = document.querySelector('meta[name="twitter:image"]');
    const twitterImageAlt = document.querySelector('meta[name="twitter:image:alt"]');
    const canonical = document.querySelector('link[rel="canonical"]');
    const origDesc = metaDescription?.getAttribute('content') || '';
    const origKeywords = metaKeywords?.getAttribute('content') || '';
    const origOgTitle = ogTitle?.getAttribute('content') || '';
    const origOgDesc = ogDescription?.getAttribute('content') || '';
    const origOgUrl = ogUrl?.getAttribute('content') || '';
    const origOgType = ogType?.getAttribute('content') || '';
    const origOgImage = ogImage?.getAttribute('content') || '';
    const origOgImageAlt = ogImageAlt?.getAttribute('content') || '';
    const origTwitterTitle = twitterTitle?.getAttribute('content') || '';
    const origTwitterDesc = twitterDescription?.getAttribute('content') || '';
    const origTwitterImage = twitterImage?.getAttribute('content') || '';
    const origTwitterImageAlt = twitterImageAlt?.getAttribute('content') || '';
    const origCanonical = canonical?.getAttribute('href') || '';
    // Update title
    document.title = 'UDSA - Universal Data Stream Analyzer | Telecom Intelligence Platform | COSMO IMAGE AVIATION';
    // Update meta description
    if (metaDescription) {
      metaDescription.setAttribute('content', 'UDSA (Universal Data Stream Analyzer): Unified telecom analytics platform integrating 2G-5G data for law enforcement. Features live tracking, messaging interception, IMEI conversion, advanced profiling, and GSM data records analysis.');
    }
    // Update keywords
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'UDSA, Universal Data Stream Analyzer, telecom analytics, law enforcement technology, live tracking, MSISDN, IMEI conversion, GSM data records, advanced profiling, intelligence platform, COSMO IMAGE AVIATION');
    }
    // Update Open Graph tags
    if (ogTitle) ogTitle.setAttribute('content', 'UDSA - Universal Data Stream Analyzer | Telecom Intelligence Platform');
    if (ogDescription) ogDescription.setAttribute('content', 'Unified telecom analytics platform integrating multi-source 2G-5G data for law enforcement threat detection, live tracking, and comprehensive post-incident analysis.');
    if (ogUrl) ogUrl.setAttribute('content', 'https://www.cosmoimageaviation.com/udsa');
    if (ogType) ogType.setAttribute('content', 'product');
    if (ogImage) ogImage.setAttribute('content', 'https://cdn.magicpatterns.com/uploads/wR6PBWtwaBZstmqNE8H5QF/Screenshot_2026-02-25_222712.png');
    if (ogImageAlt) ogImageAlt.setAttribute('content', 'UDSA Universal Data Stream Analyzer Platform');
    // Update Twitter Card tags
    if (twitterTitle) twitterTitle.setAttribute('content', 'UDSA - Universal Data Stream Analyzer | Telecom Intelligence');
    if (twitterDescription) twitterDescription.setAttribute('content', 'Unified telecom analytics platform integrating 2G-5G data for law enforcement threat detection and comprehensive post-incident analysis.');
    if (twitterImage) twitterImage.setAttribute('content', 'https://cdn.magicpatterns.com/uploads/wR6PBWtwaBZstmqNE8H5QF/Screenshot_2026-02-25_222712.png');
    if (twitterImageAlt) twitterImageAlt.setAttribute('content', 'UDSA Universal Data Stream Analyzer Platform');
    // Update canonical
    if (canonical) canonical.setAttribute('href', 'https://www.cosmoimageaviation.com/udsa');
    // Update og:image dimensions
    const ogImageWidth = document.querySelector('meta[property="og:image:width"]');
    const ogImageHeight = document.querySelector('meta[property="og:image:height"]');
    const origOgImageWidth = ogImageWidth?.getAttribute('content') || '';
    const origOgImageHeight = ogImageHeight?.getAttribute('content') || '';
    if (ogImageWidth) ogImageWidth.setAttribute('content', '1920');
    if (ogImageHeight) ogImageHeight.setAttribute('content', '1080');
    // Add page-specific JSON-LD structured data
    const jsonLd = document.createElement('script');
    jsonLd.type = 'application/ld+json';
    jsonLd.id = 'udsa-jsonld';
    jsonLd.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'UDSA - Universal Data Stream Analyzer',
      applicationCategory: 'SecurityApplication',
      operatingSystem: 'Web',
      description: 'Unified telecom analytics platform integrating multi-source 2G-5G data using advanced statistical methods to map and analyze individual and syndicate target behaviors for law enforcement.',
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceCurrency: 'USD',
        seller: {
          '@type': 'Organization',
          name: 'COSMO IMAGE AVIATION PTE. LTD.',
          url: 'https://www.cosmoimageaviation.com/'
        }
      },
      featureList: 'Live Track (CGI Location), Inbound Messaging Interception, IMEI/NIK to MSISDN Conversion, Advanced Profiler (Dark Web Data), GSM Data Records Analysis',
      screenshot: 'https://cdn.magicpatterns.com/uploads/j2CYZ6mP7jSnC1xFbzW1SA/Screenshot_2026-02-25_222727.png',
      provider: {
        '@type': 'Organization',
        name: 'COSMO IMAGE AVIATION PTE. LTD.',
        url: 'https://www.cosmoimageaviation.com/'
      }
    });
    document.head.appendChild(jsonLd);
    // Add BreadcrumbList JSON-LD
    const breadcrumbLd = document.createElement('script');
    breadcrumbLd.type = 'application/ld+json';
    breadcrumbLd.id = 'udsa-breadcrumb';
    breadcrumbLd.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.cosmoimageaviation.com/'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Products',
        item: 'https://www.cosmoimageaviation.com/#services'
      }, {
        '@type': 'ListItem',
        position: 3,
        name: 'UDSA Platform',
        item: 'https://www.cosmoimageaviation.com/udsa'
      }]
    });
    document.head.appendChild(breadcrumbLd);
    // Scroll to top on mount
    window.scrollTo(0, 0);
    // Cleanup on unmount
    return () => {
      document.title = originalTitle;
      if (metaDescription) metaDescription.setAttribute('content', origDesc);
      if (metaKeywords) metaKeywords.setAttribute('content', origKeywords);
      if (ogTitle) ogTitle.setAttribute('content', origOgTitle);
      if (ogDescription) ogDescription.setAttribute('content', origOgDesc);
      if (ogUrl) ogUrl.setAttribute('content', origOgUrl);
      if (ogType) ogType.setAttribute('content', origOgType);
      if (ogImage) ogImage.setAttribute('content', origOgImage);
      if (ogImageAlt) ogImageAlt.setAttribute('content', origOgImageAlt);
      if (ogImageWidth) ogImageWidth.setAttribute('content', origOgImageWidth);
      if (ogImageHeight) ogImageHeight.setAttribute('content', origOgImageHeight);
      if (twitterTitle) twitterTitle.setAttribute('content', origTwitterTitle);
      if (twitterDescription) twitterDescription.setAttribute('content', origTwitterDesc);
      if (twitterImage) twitterImage.setAttribute('content', origTwitterImage);
      if (twitterImageAlt) twitterImageAlt.setAttribute('content', origTwitterImageAlt);
      if (canonical) canonical.setAttribute('href', origCanonical);
      document.getElementById('udsa-jsonld')?.remove();
      document.getElementById('udsa-breadcrumb')?.remove();
    };
  }, []);
  return <div className="min-h-screen w-full bg-white font-sans" data-id="element-644">
      <Header data-id="element-645" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pb-32 px-4 bg-navy overflow-hidden isolate" data-id="element-646">
        {/* Background Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-teal/20 z-0" data-id="element-647"></div>

        {/* Pattern Overlay */}
        <div className="absolute inset-0 opacity-20 z-0" data-id="element-648">
          <div className="absolute top-[-10%] right-[-10%] w-[800px] h-[800px] bg-teal/20 rounded-full blur-[120px]" data-id="element-649"></div>
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-sky/20 rounded-full blur-[100px]" data-id="element-650"></div>
        </div>

        <div className="container mx-auto max-w-[var(--maxw)] relative z-10" data-id="element-651">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-652">
            <div className="lg:w-1/2" data-id="element-653">
              <div className="inline-block border-l-4 border-teal pl-4 mb-6" data-id="element-654">
                <span className="text-teal font-bold tracking-widest uppercase text-sm" data-id="element-655">
                  UIDE
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white leading-tight tracking-wide" data-id="element-656">
                UNIVERSAL
                <br data-id="element-657" />
                DATA STREAM
                <br data-id="element-658" />
                ANALYZER
              </h1>

              <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed font-light max-w-2xl" data-id="element-659">
                UDSA is a unified analytics hub that integrates multi-source
                2G–5G telecom data using{' '}
                <span className="text-teal font-medium" data-id="element-660">
                  advanced statistical methods to map and analyze both
                  individual and syndicate target behaviors
                </span>
                . UDSA helps law enforcement detect threats and deliver
                comprehensive post-incident analysis to prevent further
                escalation.
              </p>
            </div>

            <div className="lg:w-1/2 w-full flex justify-center lg:justify-end" data-id="element-661">
              <div className="relative w-full max-w-md lg:max-w-lg" data-id="element-662">
                <img src="https://cdn.magicpatterns.com/uploads/wR6PBWtwaBZstmqNE8H5QF/Screenshot_2026-02-25_222712.png" alt="UDSA 3D Sphere Visualization" className="w-full h-auto drop-shadow-2xl animate-float" style={{
                animation: 'float 6s ease-in-out infinite'
              }} data-id="element-663" />
              </div>
            </div>
          </div>
        </div>

        {/* Custom animation style */}
        <style data-id="element-664">{`
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
            100% { transform: translateY(0px); }
          }
        `}</style>
      </section>

      {/* How UDSA Works Section */}
      <section className="py-20 lg:py-24 bg-white" data-id="element-665">
        <div className="container mx-auto max-w-[var(--maxw)] px-4" data-id="element-666">
          <div className="text-center mb-16" data-id="element-667">
            <span className="text-teal font-bold tracking-widest uppercase text-sm block mb-4" data-id="element-668">
              WORKFLOW
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-navy mb-6" data-id="element-669">
              HOW UDSA WORKS?
            </h2>
          </div>

          <div className="w-full bg-white p-2 rounded-none shadow-2xl border border-gray-100" data-id="element-670">
            <img src="https://cdn.magicpatterns.com/uploads/j2CYZ6mP7jSnC1xFbzW1SA/Screenshot_2026-02-25_222727.png" alt="How UDSA Works Workflow Diagram" className="w-full h-auto" data-id="element-671" />
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-20 lg:py-24 bg-gray-50" data-id="element-672">
        <div className="container mx-auto max-w-[var(--maxw)] px-4" data-id="element-673">
          <div className="text-center mb-16" data-id="element-674">
            <span className="text-teal font-bold tracking-widest uppercase text-sm block mb-4" data-id="element-675">
              FEATURES
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-navy mb-6" data-id="element-676">
              CAPABILITIES
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-677">
            {/* Capability 1 */}
            <div className="bg-white border-l-4 border-teal p-10 shadow-sm hover:shadow-md transition-shadow" data-id="element-678">
              <div className="flex items-start gap-6" data-id="element-679">
                <div className="flex-shrink-0 mt-1" data-id="element-680">
                  <div className="w-14 h-14 bg-navy text-white flex items-center justify-center rounded-none" data-id="element-681">
                    <MapPinIcon className="w-7 h-7" data-id="element-682" />
                  </div>
                </div>
                <div data-id="element-683">
                  <h3 className="text-xl font-bold text-navy mb-4" data-id="element-684">
                    UDSA - Live Track
                  </h3>
                  <p className="text-gray-600 text-lg leading-relaxed" data-id="element-685">
                    The system can detect the CGI location of the target's
                    MSISDN.
                  </p>
                </div>
              </div>
            </div>

            {/* Capability 2 */}
            <div className="bg-white border-l-4 border-teal p-10 shadow-sm hover:shadow-md transition-shadow" data-id="element-686">
              <div className="flex items-start gap-6" data-id="element-687">
                <div className="flex-shrink-0 mt-1" data-id="element-688">
                  <div className="w-14 h-14 bg-navy text-white flex items-center justify-center rounded-none" data-id="element-689">
                    <MessageCircleIcon className="w-7 h-7" data-id="element-690" />
                  </div>
                </div>
                <div data-id="element-691">
                  <h3 className="text-xl font-bold text-navy mb-4" data-id="element-692">
                    UDSA - Inbound Messaging Interspector
                  </h3>
                  <p className="text-gray-600 text-lg leading-relaxed" data-id="element-693">
                    The system can perform messaging interception for a
                    specified target number.
                  </p>
                </div>
              </div>
            </div>

            {/* Capability 3 */}
            <div className="bg-white border-l-4 border-teal p-10 shadow-sm hover:shadow-md transition-shadow" data-id="element-694">
              <div className="flex items-start gap-6" data-id="element-695">
                <div className="flex-shrink-0 mt-1" data-id="element-696">
                  <div className="w-14 h-14 bg-navy text-white flex items-center justify-center rounded-none" data-id="element-697">
                    <RefreshCwIcon className="w-7 h-7" data-id="element-698" />
                  </div>
                </div>
                <div data-id="element-699">
                  <h3 className="text-xl font-bold text-navy mb-4" data-id="element-700">
                    UDSA - Conversion
                  </h3>
                  <p className="text-gray-600 text-lg leading-relaxed" data-id="element-701">
                    The system can convert IMEI and NIK information into MSISDNs
                    for input into the system.
                  </p>
                </div>
              </div>
            </div>

            {/* Capability 4 */}
            <div className="bg-white border-l-4 border-teal p-10 shadow-sm hover:shadow-md transition-shadow" data-id="element-702">
              <div className="flex items-start gap-6" data-id="element-703">
                <div className="flex-shrink-0 mt-1" data-id="element-704">
                  <div className="w-14 h-14 bg-navy text-white flex items-center justify-center rounded-none" data-id="element-705">
                    <UserSearchIcon className="w-7 h-7" data-id="element-706" />
                  </div>
                </div>
                <div data-id="element-707">
                  <h3 className="text-xl font-bold text-navy mb-4" data-id="element-708">
                    UDSA - Advanced Profiler
                  </h3>
                  <p className="text-gray-600 text-lg leading-relaxed" data-id="element-709">
                    The system can display data profile target based on offline
                    leak / data breach from dark web.
                  </p>
                </div>
              </div>
            </div>

            {/* Capability 5 */}
            <div className="bg-white border-l-4 border-teal p-10 shadow-sm hover:shadow-md transition-shadow md:col-span-2 md:w-1/2 md:mx-auto" data-id="element-710">
              <div className="flex items-start gap-6" data-id="element-711">
                <div className="flex-shrink-0 mt-1" data-id="element-712">
                  <div className="w-14 h-14 bg-navy text-white flex items-center justify-center rounded-none" data-id="element-713">
                    <PhoneIcon className="w-7 h-7" data-id="element-714" />
                  </div>
                </div>
                <div data-id="element-715">
                  <h3 className="text-xl font-bold text-navy mb-4" data-id="element-716">
                    UDSA - GSM Data Records
                  </h3>
                  <p className="text-gray-600 text-lg leading-relaxed" data-id="element-717">
                    The system can display the history of phone call and SMS
                    aggregation performed by the target.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-24 bg-gray-50 relative" data-id="element-718">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-navy via-teal to-navy" data-id="element-719"></div>
        <div className="container mx-auto max-w-4xl text-center px-4" data-id="element-720">
          <h2 className="text-3xl md:text-5xl font-bold text-navy mb-8" data-id="element-721">
            Ready to Enhance Your Intelligence Capabilities?
          </h2>
          <p className="text-xl text-gray-600 mb-12" data-id="element-722">
            Contact us for a demo and further consultation on UDSA platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-723">
            <a href="mailto:contact@cosmoimageaviation.co" className="bg-teal hover:bg-teal/90 text-white font-bold py-4 px-10 rounded-none transition-all shadow-lg" data-id="element-724">
              Contact Us
            </a>
            <a href="https://drive.google.com/drive/folders/1sy0dPG2IWa4o2TwxTa_c1nI2HFo7yF4f?usp=sharing" target="_blank" rel="noopener noreferrer" className="bg-white border-2 border-navy hover:bg-navy hover:text-white text-navy font-bold py-4 px-10 rounded-none transition-all inline-flex items-center justify-center gap-2" data-id="element-725">
              <DownloadIcon className="w-5 h-5" data-id="element-726" />
              Download Brochure
            </a>
          </div>
        </div>
      </section>

      <Footer data-id="element-727" />
    </div>;
}